import { useEffect, useState } from "react";
import { SEO } from "../components/SEO";
import { apiFetch } from "../lib/api";

interface CaseStudy {
  id: string;
  title: string;
  slug: string;
  clientName: string;
  clientSegment: string;
  clientSize: string;
  challenge: string;
  coverImage: string | null;
  metrics: string;
  publishedAt: string;
}

const segmentLabels: Record<string, string> = {
  varejo: "Varejo",
  industria: "Indústria",
  servicos: "Serviços",
  ecommerce: "E-commerce",
  franquias: "Franquias",
};

const sizeLabels: Record<string, string> = {
  micro: "Micro",
  small: "Pequena",
  medium: "Média",
  large: "Grande",
};

export default function CaseStudiesPage() {
  const [cases, setCases] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSegment, setSelectedSegment] = useState<string | null>(null);

  useEffect(() => {
    apiFetch("/api/cases")
      .then((json) => {
        if (json.success) {
          setCases(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const segments = [...new Set(cases.map((c) => c.clientSegment))];

  const filteredCases = selectedSegment
    ? cases.filter((c) => c.clientSegment === selectedSegment)
    : cases;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title="Casos de Sucesso"
        description="Veja como empresas transformaram suas operações com o ZWeb. Cases reais de sucesso em varejo, indústria, serviços e e-commerce."
        keywords={["casos de sucesso", "cases", "zweb", "clientes", "resultados"]}
        canonicalUrl="/casos-de-sucesso"
      />

      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/" className="text-sm font-medium hover:text-accent-600">Home</a>
            <a href="/blog" className="text-sm font-medium hover:text-accent-600">Blog</a>
            <a href="/tutoriais" className="text-sm font-medium hover:text-accent-600">Tutoriais</a>
            <a href="/casos-de-sucesso" className="text-sm font-medium text-accent-600">Cases</a>
          </nav>
        </div>
      </header>

      <main className="container py-12">
        <div className="text-center">
          <h1 className="text-4xl font-bold">Casos de Sucesso</h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            Veja como empresas transformaram suas operações com o ZWeb.
          </p>
        </div>

        {/* Filtros */}
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => setSelectedSegment(null)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              selectedSegment === null
                ? "bg-accent-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            Todos
          </button>
          {segments.map((segment) => (
            <button
              key={segment}
              onClick={() => setSelectedSegment(segment)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                selectedSegment === segment
                  ? "bg-accent-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
              }`}
            >
              {segmentLabels[segment] || segment}
            </button>
          ))}
        </div>

        {/* Lista de cases */}
        {loading ? (
          <div className="mt-12 text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-950">
            <p className="text-gray-600 dark:text-gray-400">
              Nenhum caso de sucesso encontrado neste segmento.
            </p>
          </div>
        ) : (
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {filteredCases.map((caseStudy) => {
              const metrics: Record<string, string> = caseStudy.metrics ? JSON.parse(caseStudy.metrics) : {};
              return (
                <a
                  key={caseStudy.id}
                  href={`/casos-de-sucesso/${caseStudy.slug}`}
                  className="group rounded-2xl border border-gray-200 bg-white p-8 shadow-sm transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-950"
                >
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800 dark:bg-accent-900 dark:text-accent-300">
                      {segmentLabels[caseStudy.clientSegment] || caseStudy.clientSegment}
                    </span>
                    <span className="text-sm text-gray-500">
                      {sizeLabels[caseStudy.clientSize] || caseStudy.clientSize}
                    </span>
                  </div>

                  <h2 className="mt-4 text-2xl font-bold group-hover:text-accent-600">
                    {caseStudy.title}
                  </h2>

                  <div className="mt-2 text-lg font-medium text-gray-700 dark:text-gray-300">
                    {caseStudy.clientName}
                  </div>

                  <p className="mt-4 text-gray-600 dark:text-gray-400 line-clamp-3">
                    {caseStudy.challenge}
                  </p>

                  {/* Métricas */}
                  {Object.keys(metrics).length > 0 && (
                    <div className="mt-6 grid grid-cols-3 gap-4">
                      {Object.entries(metrics).slice(0, 3).map(([key, value]) => (
                        <div key={key} className="text-center">
                          <div className="text-2xl font-bold text-accent-600">{value}</div>
                          <div className="text-xs text-gray-500">{key}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 text-sm font-medium text-accent-600 group-hover:underline">
                    Ler caso completo →
                  </div>
                </a>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 rounded-2xl bg-gradient-to-br from-accent-500 to-violet-600 p-12 text-center text-white">
          <h3 className="text-3xl font-bold">Sua empresa pode ser o próximo caso de sucesso</h3>
          <p className="mx-auto mt-4 max-w-2xl text-accent-100">
            Junte-se a centenas de empresas que já transformaram suas operações com o ZWeb.
          </p>
          <a
            href="/solicitar-demo"
            className="mt-8 inline-block rounded-full bg-white px-8 py-4 text-lg font-medium text-accent-600 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-white/20"
          >
            Solicitar Demonstração
          </a>
        </div>
      </main>

      <footer className="border-t border-gray-200 bg-white py-8 dark:border-gray-800 dark:bg-gray-950">
        <div className="container text-center text-sm text-gray-600 dark:text-gray-400">
          © 2026 ZWeb. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}
